import { useEffect, useState } from "react";
import { FaDownload } from "react-icons/fa";
import domain from "../../../../reusecomponent/domain";
import { getMedia, getMediafixtid, saveMedia } from "../mediaCache";
import { WorningToastContainer } from "../../../function/CustomFunction";
import { useSelector } from "react-redux";

// Shared helper — defined once, used by both Document and Documents
const MIME_TO_EXTENSION = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "audio/mpeg": "mp3",
  "audio/mp4": "m4a",
  "application/pdf": "pdf",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/zip": "zip",
};

const getExtensionFromBlob = (blob) => MIME_TO_EXTENSION[blob.type] || "file";

// ─── Document ─────────────────────────────────────────────────────────────────
// Used inside the chat message bubble (full download button).
export function Document({ chat, sending_type }) {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [extension, setExtension] = useState(null);
  const fileid = chat.file_id;
  const Accesstoken = useSelector((state) => state.Token.value);

  const loadDocument = async () => {
    setLoading(true);
    setBlobUrl(null);
    setExtension(null);

    try {
      if (!fileid) { setLoading(false); return; }

      const localBlob = await getMedia(chat.body_type, chat.type, fileid);
      if (localBlob.exists) {
        if (localBlob.value == null) { setLoading(false); return; }
        setBlobUrl(URL.createObjectURL(localBlob.value));
        setExtension(getExtensionFromBlob(localBlob.value));
        return;
      }

      const fileResponse = await fetch(
        `${domain()}/whatsapp/${sending_type}/document/${fileid}`,
        { method: "GET", headers: { Authorization: `Bearer ${Accesstoken}` } }
      );
      if (fileResponse.ok) {
        const blob = await fileResponse.blob();
        await saveMedia(chat.body_type, chat.type, fileid, blob);
        setBlobUrl(URL.createObjectURL(blob));
        setExtension(getExtensionFromBlob(blob));
      } else {
        WorningToastContainer("File not found on server!");
        await saveMedia(chat.body_type, chat.type, fileid, null);
      }
    } catch (_) {
      WorningToastContainer("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocument();
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileid]);

  const triggerDownload = () => {
    if (!blobUrl || loading) return;
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${fileid}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isImage = ["jpg", "png", "gif", "webp"].includes(extension);
  const isVideo = ["webm", "mp4"].includes(extension);

  return (
    <div style={{ marginBottom: "15px" }}>
      <button
        onClick={triggerDownload}
        disabled={loading}
        aria-label={loading ? "Loading document" : `Download ${extension || "file"}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px 18px",
          borderRadius: "40px",
          border: "2px solid #1EC1F9",
          background: "transparent",
          color: "gray",
          fontSize: "16px",
          cursor: loading ? "not-allowed" : "pointer",
        }}
      >
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "#1EC1F9",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {loading ? (
            <div
              style={{
                width: "18px",
                height: "18px",
                border: "3px solid white",
                borderTop: "3px solid transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          ) : (
            <FaDownload size={18} color="white" />
          )}
        </div>

        {loading ? "Please wait…" : "Download"}

        {isImage && blobUrl && (
          <img
            src={blobUrl}
            alt="document preview"
            style={{ width: "40px", height: "30px", cursor: "pointer", borderRadius: "10px", objectFit: "cover" }}
          />
        )}
        {isVideo && blobUrl && (
          <video
            src={blobUrl}
            style={{ width: "40px", height: "30px", cursor: "pointer", borderRadius: "10px", objectFit: "cover" }}
            muted
            disablePictureInPicture
            onMouseOver={e => e.target.play()}
            onMouseOut={e => e.target.pause()}
          />
        )}
      </button>
    </div>
  );
}

// ─── Documents ────────────────────────────────────────────────────────────────
// Used inside the MediaTabs panel (compact thumbnail, cache-only).
export function Documents({ chat }) {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [extension, setExtension] = useState(null);
  const fileid = chat.file_id;

  const loadDocument = async () => {
    setLoading(true);
    setBlobUrl(null);
    setExtension(null);

    try {
      if (!fileid) { setLoading(false); return; }
      const localBlob = await getMediafixtid(chat.body_type, fileid);
      if (localBlob.exists) {
        if (localBlob.value == null) { setLoading(false); return; }
        setBlobUrl(URL.createObjectURL(localBlob.value));
        setExtension(getExtensionFromBlob(localBlob.value));
        return;
      }
    } catch (_) {
      WorningToastContainer("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocument();
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileid]);

  // FIXED: removed noop setTimeout(5000)
  const triggerDownload = () => {
    if (!blobUrl || loading) return;
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${fileid}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isImage = ["jpg", "png", "gif", "webp"].includes(extension);
  const isVideo = ["webm", "mp4"].includes(extension);

  return (
    <button
      onClick={triggerDownload}
      disabled={loading}
      aria-label={loading ? "Loading" : `Download ${extension || "file"}`}
      style={{
        height: "65px",
        aspectRatio: "1 / 1",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        borderRadius: "40px",
        padding: "0 8px",
        border: "1px solid #1EC1F9",
        background: "transparent",
        color: "gray",
        fontSize: "16px",
        cursor: loading ? "not-allowed" : "pointer",
      }}
    >
      <div
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          background: "#1EC1F9",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {loading ? (
          <div
            style={{
              width: "18px",
              height: "18px",
              border: "3px solid white",
              borderTop: "3px solid transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        ) : (
          <FaDownload size={18} color="white" />
        )}
      </div>

      {isImage && blobUrl && (
        <img
          src={blobUrl}
          alt="doc preview"
          style={{ width: "40px", height: "30px", cursor: "pointer", borderRadius: "10px", objectFit: "cover" }}
        />
      )}
      {isVideo && blobUrl && (
        <video
          src={blobUrl}
          style={{ width: "40px", height: "30px", cursor: "pointer", borderRadius: "10px", objectFit: "cover" }}
          muted
          disablePictureInPicture
          onMouseOver={e => e.target.play()}
          onMouseOut={e => e.target.pause()}
        />
      )}
    </button>
  );
}